import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage, useIntl } from 'react-intl';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import { Order, OrderStatus, PaymentMethodType } from '../../../../models';
import DateTime from '../../../../utils/DateTime';

const useStyles = makeStyles(({
  header: {
    padding: '24px 30px 0',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: '14px 24px',
  },
  headerMeta: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 14,
    fontSize: 15,
    color: '#16141D',
  },
  metaLabel: {
    fontWeight: 700,
    color: '#3C3A50',
  },
  metaSeparator: {
    color: '#CDD6D2',
  },
  statusWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  statusLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: '#3C3A50',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '5px 13px',
    borderRadius: 999,
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: '.07em',
    textTransform: 'uppercase',
  },
  paymentMethodRow: {
    marginTop: 12,
    paddingBottom: 24,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 15,
    color: '#16141D',
  },
  paymentMethodValue: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  },
  paymentMethodIcon: {
    display: 'inline-flex',
    color: '#5F5D75',
    fontSize: 18,
  },
  actionRequiredContainer: {
    margin: '0 30px 22px',
    padding: '15px 17px',
    borderRadius: 12,
    backgroundColor: '#FDF4E6',
    border: '1px solid #F0D9AD',
  },
  actionRequiredText: {
    fontSize: 14.5,
    lineHeight: 1.5,
    color: '#8A5A12',
    margin: 0,
  },
  actionRequiredSubText: {
    fontSize: 13.3,
    lineHeight: 1.5,
    color: '#5F5D75',
    margin: '5px 0 0',
  },
}));

// Badge palette per Billing 2.0 design: paid → green, failed states → red,
// everything in-flight or inert → neutral gray.
const getStatusBadgeColors = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.PAID:
      return { background: '#E7F6EF', color: '#0F8461', border: '#BFE6D5' };
    case OrderStatus.FAIL:
    case OrderStatus.PASS_DUE:
      return { background: '#FBECEB', color: '#C0392B', border: '#F0C8C5' };
    case OrderStatus.CANCEL:
      return { background: '#EEF2F0', color: '#5B6661', border: '#E1E7E4' };
    default:
      return { background: '#EEF2F0', color: '#3C4642', border: '#E1E7E4' };
  }
};

export const OrderSummarize = ({ order }: { order: Order }) => {
  const classes = useStyles();
  const intl = useIntl();

  const badgeColors = getStatusBadgeColors(order.status);
  const checkRequiredActionOrder = order.status === OrderStatus.PENDING
    && order.paymentMethodType === PaymentMethodType.CREDIT_CARD
    && !!order.stripeInvoiceUrl;

  const renderRequiredAction = () => (
    <div className={classes.actionRequiredContainer}>
      <p className={classes.actionRequiredText}>
        <FormattedMessage
          id="page.order.history.order.action-require.title"
          values={{
            title: (
              <strong>Action required:</strong>
            ),
          }}
        />
      </p>
      <p className={classes.actionRequiredSubText}>
        <FormattedMessage id="page.order.history.order.action-require.description" />
      </p>
    </div>
  );

  return (
    <Box
      id={`order-${order.id}-summarize`}
      display="flex"
      flexDirection="column"
    >
      <div className={classes.header}>
        <div className={classes.headerRow}>
          <div className={classes.headerMeta}>
            <span>
              <span className={classes.metaLabel}>
                <FormattedMessage id="page.order.history.order.title" />
              </span>
              {' '}
              <span id={`order-${order.id}-number`}>{`#${order.id}`}</span>
            </span>
            <span className={classes.metaSeparator}>|</span>
            <span>
              <span className={classes.metaLabel}>
                <FormattedMessage id="page.order.history.order.created.title" />
              </span>
              {' '}
              {DateTime.formatDate(intl, order.orderDate)}
            </span>
          </div>
          <div className={classes.statusWrap}>
            <Typography component="span" className={classes.statusLabel}>
              <FormattedMessage id="page.order.history.order.status" />
            </Typography>
            <span
              id={`order-${order.id}-status-badge`}
              className={classes.statusBadge}
              style={{
                backgroundColor: badgeColors.background,
                color: badgeColors.color,
                border: `1px solid ${badgeColors.border}`,
              }}
            >
              <FormattedMessage id={`page.order.history.order.status.${order.status.toLowerCase()}.name`} />
            </span>
          </div>
        </div>

        <div className={classes.paymentMethodRow}>
          <span className={classes.metaLabel}>
            <FormattedMessage id="page.order.history.order.payment-method" />
          </span>
          <span className={classes.paymentMethodValue}>
            <span className={classes.paymentMethodIcon}>
              {order.paymentMethodType === PaymentMethodType.BANK_TRANSFER
                ? <AccountBalanceOutlinedIcon fontSize="inherit" />
                : <CreditCardOutlinedIcon fontSize="inherit" />}
            </span>
            <FormattedMessage
              id={`page.order.history.order.payment-method.${order.paymentMethodType.toLowerCase()}.name`}
            />
          </span>
        </div>
      </div>

      {checkRequiredActionOrder && renderRequiredAction()}
    </Box>
  );
};
